/**
 * "Open in Browser" for a drive item — opens the file in a new tab rather than
 * downloading it.
 *
 * Why this isn't just `window.open(item.fileUrl)`: `fileUrl` is a signed Azure
 * blob URL, and browsers have no renderer for OOXML, so a .docx URL is always a
 * download. SharePoint-backed files already avoid this because `webUrl` points at
 * Office Online. For everything else we route Office formats through our own
 * read-only viewer, which streams the bytes and renders them with SuperDoc.
 *
 * Formats the browser renders natively (PDF, images, text) keep going straight to
 * the signed URL — the viewer would add nothing there.
 */

/** Office formats that a browser will download rather than render. */
const VIEWER_EXTENSIONS = ["doc", "docx", "docm", "dot", "dotx"];

const extensionOf = (name?: string): string =>
  (name || "").split(".").pop()?.toLowerCase() || "";

export const needsInAppViewer = (name?: string): boolean =>
  VIEWER_EXTENSIONS.includes(extensionOf(name));

/**
 * @returns `true` if a tab was opened, `false` if the item had no openable target
 *          (so callers can surface their own message).
 */
export const openFileInBrowser = (item: {
  _id?: string;
  name?: string;
  fileUrl?: string;
  webUrl?: string | null;
  storage?: { sharepoint?: { webUrl?: string } };
}): boolean => {
  // SharePoint already renders in Office Online — nothing to improve on.
  // `webUrl` is the flattened field the API actually sends (toDriveItemResponse drops
  // `storage`); the nested read stays as a fallback for any caller passing a raw model.
  const sharepointUrl = item.webUrl || item.storage?.sharepoint?.webUrl;
  if (sharepointUrl) {
    window.open(sharepointUrl, "_blank", "noopener,noreferrer");
    return true;
  }

  if (item._id && needsInAppViewer(item.name)) {
    const query = item.name ? `?name=${encodeURIComponent(item.name)}` : "";
    window.open(`/files/view/${item._id}${query}`, "_blank", "noopener,noreferrer");
    return true;
  }

  if (item.fileUrl) {
    window.open(item.fileUrl, "_blank", "noopener,noreferrer");
    return true;
  }

  return false;
};
